import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ListingCriteriaBar from './ListingCriteriaBar'
import { sellerVerificationAPI } from '../api/client'
import type { CatalogFilterState, CatalogSort } from '../hooks/useCatalogFilters'

type Props = {
  filters: CatalogFilterState
  onPatch: (next: Partial<CatalogFilterState>) => void
  onClearAll?: () => void
  hasActiveFilters?: boolean
}

export default function CatalogSearchStrip({
  filters,
  onPatch,
  onClearAll,
  hasActiveFilters,
}: Props) {
  const { t } = useTranslation()
  const [cityOptions, setCityOptions] = useState<string[]>([])

  useEffect(() => {
    sellerVerificationAPI
      .getCities()
      .then((res) => setCityOptions((res.data?.cities as string[]) ?? []))
      .catch(() => setCityOptions([]))
  }, [])

  return (
    <div className="catalog-filters-panel">
      <div className="catalog-filters-panel__primary">
        <select
          className="catalog-filter-control catalog-filter-control--select catalog-filter-control--sort"
          value={filters.sortBy}
          onChange={(e) => onPatch({ sortBy: e.target.value as CatalogSort })}
          aria-label={t('catalog.sortLabel')}
        >
          <option value="newest">{t('catalog.sortNewest')}</option>
          <option value="price_asc">{t('catalog.sortPriceAsc')}</option>
          <option value="price_desc">{t('catalog.sortPriceDesc')}</option>
          <option value="model">{t('catalog.sortModel')}</option>
        </select>
      </div>

      <div className="catalog-filters-panel__fields">
        <div className="catalog-filters-panel__row catalog-filters-panel__row--duo">
          <label className="catalog-filter-field">
            <span className="catalog-filter-field__label">{t('catalog.priceFrom')}</span>
            <input
              className="catalog-filter-control"
              type="number"
              inputMode="decimal"
              min={0}
              step={1}
              placeholder="€ min"
              value={filters.priceMin}
              onChange={(e) => onPatch({ priceMin: e.target.value })}
              aria-label={t('catalog.priceFrom')}
            />
          </label>
          <label className="catalog-filter-field">
            <span className="catalog-filter-field__label">{t('catalog.priceTo')}</span>
            <input
              className="catalog-filter-control"
              type="number"
              inputMode="decimal"
              min={0}
              step={1}
              placeholder="€ max"
              value={filters.priceMax}
              onChange={(e) => onPatch({ priceMax: e.target.value })}
              aria-label={t('catalog.priceTo')}
            />
          </label>
        </div>
        <div className="catalog-filters-panel__row catalog-filters-panel__row--duo">
          <label className="catalog-filter-field">
            <span className="catalog-filter-field__label">{t('catalog.yearFrom')}</span>
            <input
              className="catalog-filter-control"
              type="number"
              inputMode="numeric"
              min={2007}
              max={2035}
              placeholder={t('catalog.yearPlaceholder')}
              value={filters.yearFrom}
              onChange={(e) => onPatch({ yearFrom: e.target.value })}
              aria-label={t('catalog.yearFrom')}
            />
          </label>
          <label className="catalog-filter-field">
            <span className="catalog-filter-field__label">{t('catalog.yearTo')}</span>
            <input
              className="catalog-filter-control"
              type="number"
              inputMode="numeric"
              min={2007}
              max={2035}
              placeholder={t('catalog.yearPlaceholder')}
              value={filters.yearTo}
              onChange={(e) => onPatch({ yearTo: e.target.value })}
              aria-label={t('catalog.yearTo')}
            />
          </label>
        </div>

        <ListingCriteriaBar
          compact
          unified
          cityOptions={cityOptions}
          value={{
            city: filters.city,
            condition: filters.condition,
            warranty: filters.warranty,
            usage: filters.usage,
            batteryMin: filters.batteryMin,
            inBox: filters.inBox,
          }}
          onChange={(next) =>
            onPatch({
              city: next.city,
              condition: next.condition,
              warranty: next.warranty,
              usage: next.usage,
              batteryMin: next.batteryMin,
              inBox: next.inBox,
            })
          }
        />

        {hasActiveFilters && onClearAll ? (
          <button type="button" className="catalog-filters-panel__clear" onClick={onClearAll}>
            {t('catalog.clearAllFilters')}
          </button>
        ) : null}
      </div>
    </div>
  )
}
